import { devProxyRouteForApiPath, fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { extractAccessTokenFromLoginJson } from '../api/authLoginResponse';
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import { friendlyHttpFailureSummary } from '../api/httpErrorFormat';
import { extractRefreshTokenFromUnknown, clearRefreshUnavailable404 } from '../api/authRefresh';
import { decodeJwtPayload } from '../auth/jwtPayload';
import {
  ASSET_OWNER_APPLE_LOGIN_PATHS,
  ASSET_OWNER_APPLE_REGISTER_PATHS,
} from './assetOwnerAppleApiPaths';
import { supportsGoogleLiteRegister } from './authEntryPolicy';
import { LOGIN_OPTIONS, canonicalAssetOwnerLoginType, type LoginOption } from './loginConstants';
import { isCapacitorNative } from '../utils/isCapacitorNative';

const APPLE_AUTH_SCRIPT_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

/** Apple Services ID for web (NOT the App ID `ai.offizz.app`). */
export const DEFAULT_APPLE_SERVICES_CLIENT_ID = 'ai.offizz.dev';

/**
 * Must match Apple Developer → Services ID → Return URLs exactly.
 * Localhost is not allowed — Apple popup still uses this HTTPS URL when CRA runs on :3020.
 */
export const DEFAULT_APPLE_REDIRECT_URI = 'https://dev.offizz.ai/auth/apple/callback';

export function resolveAppleClientId(): string | null {
  const fromEnv = process.env.REACT_APP_APPLE_CLIENT_ID?.trim();
  return fromEnv || DEFAULT_APPLE_SERVICES_CLIENT_ID;
}

const APP_APPLE_REDIRECT_HOSTS = new Set(['app.offizz.ai', 'app-next.offizz.ai']);

/** Must match Apple Developer → Services ID → Return URLs exactly (HTTPS; no localhost). */
export function resolveAppleRedirectUri(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname.toLowerCase();
    if (APP_APPLE_REDIRECT_HOSTS.has(host) && window.location.origin) {
      return `${window.location.origin.replace(/\/$/, '')}/auth/apple/callback`;
    }
  }
  const fromEnv = process.env.REACT_APP_APPLE_REDIRECT_URI?.trim();
  return fromEnv || DEFAULT_APPLE_REDIRECT_URI;
}

const APPLE_PENDING_KEY = 'ofrontend.appleSignInPending';

/** Name/email from Apple's one-time `user` payload on first authorization. */
export type AppleFirstSignInUser = {
  email?: string;
  name?: {
    firstName?: string;
    lastName?: string;
  };
};

export type AppleRedirectCallback = {
  idToken: string | null;
  state: string | null;
  error: string | null;
  user?: AppleFirstSignInUser;
};

function isLoginOption(value: string): value is LoginOption {
  return (LOGIN_OPTIONS as readonly string[]).includes(value);
}

export function createAppleOAuthState(loginType?: LoginOption): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  const nonce = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  if (!loginType) {
    return nonce;
  }
  return `${nonce}.${encodeURIComponent(loginType)}`;
}

export function loginTypeFromAppleOAuthState(state: string | null | undefined): LoginOption | null {
  if (!state) {
    return null;
  }
  const separator = state.indexOf('.');
  if (separator < 0) {
    return null;
  }
  let raw = state.slice(separator + 1);
  try {
    raw = decodeURIComponent(raw);
  } catch {
    return null;
  }
  return isLoginOption(raw) ? raw : null;
}

export function appleOAuthStateNonce(state: string | null | undefined): string | null {
  if (!state) {
    return null;
  }
  const separator = state.indexOf('.');
  const nonce = (separator < 0 ? state : state.slice(0, separator)).trim();
  return nonce || null;
}

export function persistAppleSignInPending(loginType: LoginOption, state: string): void {
  try {
    sessionStorage.setItem(APPLE_PENDING_KEY, JSON.stringify({ loginType, state }));
  } catch {
    /* quota / private mode */
  }
}

export function takeAppleSignInPending(): { loginType: LoginOption; state: string } | null {
  try {
    const raw = sessionStorage.getItem(APPLE_PENDING_KEY);
    sessionStorage.removeItem(APPLE_PENDING_KEY);
    if (!raw?.trim()) {
      return null;
    }
    const parsed = JSON.parse(raw) as { loginType?: unknown; state?: unknown };
    if (typeof parsed.loginType !== 'string' || !isLoginOption(parsed.loginType)) {
      return null;
    }
    if (typeof parsed.state !== 'string' || !parsed.state.trim()) {
      return null;
    }
    return { loginType: parsed.loginType, state: parsed.state };
  } catch {
    return null;
  }
}

/** Apple JS popups open a blank Chrome tab from the Capacitor WebView. */
export function shouldUseAppleJsPopup(): boolean {
  return !isCapacitorNative();
}

export function isAppleCallbackPath(pathname: string): boolean {
  const path = pathname.split('?')[0]?.replace(/\/+$/, '') ?? '';
  return path.endsWith('/auth/apple/callback');
}

export function buildAppleAuthorizeUrl(
  clientId: string,
  redirectURI: string,
  state: string,
): string {
  // Do not request `name` / `email` here. Apple then requires response_mode=form_post,
  // and this SPA cannot read a POST body. Email still arrives in the id_token JWT
  // when Apple includes it (typical after the user has authorized Offizz Web).
  const query = [
    `client_id=${encodeURIComponent(clientId)}`,
    `redirect_uri=${encodeURIComponent(redirectURI)}`,
    `response_type=${encodeURIComponent('code id_token')}`,
    'response_mode=fragment',
    `state=${encodeURIComponent(state)}`,
  ].join('&');
  return `https://appleid.apple.com/auth/authorize?${query}`;
}

function parseAppleUserPayload(raw: string | null): AppleFirstSignInUser | undefined {
  if (!raw?.trim()) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as AppleFirstSignInUser;
    if (!parsed || typeof parsed !== 'object') {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

export function parseAppleCallbackParams(search: string, hash: string): AppleRedirectCallback {
  const fromSearch = search.startsWith('?') ? search.slice(1) : search;
  const fromHash = hash.startsWith('#') ? hash.slice(1) : hash;
  const params = new URLSearchParams(fromHash || fromSearch);
  const idToken = params.get('id_token')?.trim() || null;
  const state = params.get('state')?.trim() || null;
  const error = params.get('error')?.trim() || null;
  const user = parseAppleUserPayload(params.get('user'));
  return { idToken, state, error, user };
}

export function takeAppleCallbackFromWindow(): AppleRedirectCallback | null {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!isAppleCallbackPath(window.location.pathname)) {
    return null;
  }
  const parsed = parseAppleCallbackParams(window.location.search, window.location.hash);
  try {
    window.history.replaceState(window.history.state, document.title, '/auth');
  } catch {
    /* ignore */
  }
  return parsed;
}

function appleLoginPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_APPLE_LOGIN_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    // Backend: POST /login/clients/apple (or /login/apple) — auto-registers private clients.
    'private client': ['login/clients/apple', 'login/apple', 'login/PrivateClient/apple'],
    'business client': ['login/BusinessClient/apple', 'login/clients/apple', 'login/apple'],
    'business worker': ['login/BusinessWorker/apple', 'login/clients/apple', 'login/apple'],
    'asset owner': [...ASSET_OWNER_APPLE_LOGIN_PATHS],
    'business asset owner': [...ASSET_OWNER_APPLE_LOGIN_PATHS],
    'private asset owner': [...ASSET_OWNER_APPLE_LOGIN_PATHS],
  };
  const paths = [configured, ...(byRole[loginType] ?? ['login/clients/apple', 'login/apple'])].filter(
    (value): value is string => Boolean(value?.trim()),
  );
  return Array.from(new Set(paths));
}

export function appleLoginPathCandidatesForTests(loginType: LoginOption): string[] {
  return appleLoginPathCandidates(loginType);
}

function appleRegisterPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_APPLE_REGISTER_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['register/PrivateClient/apple', 'register/PrivateClient'],
    'business client': ['register/BusinessClient/apple', 'register/BusinessClient'],
    'business worker': ['register/BusinessWorker/apple', 'register/BusinessWorker'],
    'asset owner': [...ASSET_OWNER_APPLE_REGISTER_PATHS],
    'business asset owner': [...ASSET_OWNER_APPLE_REGISTER_PATHS],
    'private asset owner': [...ASSET_OWNER_APPLE_REGISTER_PATHS],
  };
  const paths = [configured, ...(byRole[loginType] ?? [])].filter((value): value is string =>
    Boolean(value?.trim()),
  );
  return Array.from(new Set(paths));
}

export function appleRegisterPathCandidatesForTests(loginType: LoginOption): string[] {
  return appleRegisterPathCandidates(loginType);
}

let appleScriptPromise: Promise<void> | null = null;

export function loadAppleSignInScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Apple Sign-In is only available in the browser'));
  }
  if (window.AppleID?.auth) {
    return Promise.resolve();
  }
  if (!appleScriptPromise) {
    appleScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-offizz-apple-auth="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Could not load Apple Sign-In')), {
          once: true,
        });
        return;
      }
      const script = document.createElement('script');
      script.src = APPLE_AUTH_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.dataset.offizzAppleAuth = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Could not load Apple Sign-In'));
      document.head.appendChild(script);
    });
  }
  return appleScriptPromise;
}

let appleInitializedKey: string | null = null;

export function ensureAppleSignInInitialized(clientId: string, redirectURI: string): void {
  const apple = window.AppleID?.auth;
  if (!apple || !clientId.trim() || !redirectURI.trim()) {
    return;
  }
  const key = `${clientId}::${redirectURI}`;
  if (appleInitializedKey === key) {
    return;
  }
  apple.init({
    clientId,
    scope: 'name email',
    redirectURI,
    usePopup: shouldUseAppleJsPopup(),
  });
  appleInitializedKey = key;
}

/** @internal */
export function resetAppleSignInForTests(): void {
  appleInitializedKey = null;
  appleScriptPromise = null;
}

export type AppleIdTokenProfile = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  subject: string | null;
};

function pickAppleString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export function emailFromAppleCredential(credential: string): string {
  const payload = decodeJwtPayload(credential);
  return typeof payload?.email === 'string' ? payload.email.trim() : '';
}

export function appleProfileFromCredential(
  credential: string,
  firstSignInUser?: AppleFirstSignInUser | null,
): AppleIdTokenProfile {
  const payload = decodeJwtPayload(credential);
  const jwtEmail = pickAppleString(payload?.email) ?? '';
  const email = pickAppleString(firstSignInUser?.email) ?? jwtEmail;
  const firstName = pickAppleString(firstSignInUser?.name?.firstName);
  const lastName = pickAppleString(firstSignInUser?.name?.lastName);
  const fullName =
    ([firstName, lastName].filter(Boolean).join(' ').trim() || null) ?? (email || null);
  const subject = pickAppleString(payload?.sub);
  return {
    email,
    firstName,
    lastName,
    fullName,
    subject,
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Merge Apple claims into a login response when the API omitted them. */
export function enrichAuthBodyWithAppleProfile(
  body: unknown,
  apple: AppleIdTokenProfile,
): Record<string, unknown> {
  const base = isPlainObject(body) ? { ...body } : {};
  if (!pickAppleString(base.email) && apple.email) {
    base.email = apple.email;
  }
  if (!pickAppleString(base.first_name) && apple.firstName) {
    base.first_name = apple.firstName;
  }
  if (!pickAppleString(base.last_name) && apple.lastName) {
    base.last_name = apple.lastName;
  }
  if (!pickAppleString(base.full_name) && apple.fullName) {
    base.full_name = apple.fullName;
  }
  return base;
}

export type AppleLoginApiResult = {
  response: Response;
  urlUsed: string;
  responseText: string;
  parsed: unknown;
  accessToken: string | null;
  pathUsed: string;
  flowUsed: 'login' | 'register';
};

export function appleLoginErrorMessage(result: Pick<AppleLoginApiResult, 'responseText'>): string {
  if (!result.responseText.trim()) {
    return '';
  }
  try {
    const parsed = JSON.parse(result.responseText) as { message?: unknown };
    return typeof parsed.message === 'string' ? parsed.message.trim() : '';
  } catch {
    return '';
  }
}

export function isAppleUserMissingError(
  result: Pick<AppleLoginApiResult, 'response' | 'responseText'>,
): boolean {
  if (result.response.status !== 401) {
    return false;
  }
  const message = appleLoginErrorMessage(result).toLowerCase();
  return /user does not exist|user not found|account not found|no user|not registered/.test(message);
}

export function isAppleRegisterConflictError(
  result: Pick<AppleLoginApiResult, 'response' | 'responseText'>,
): boolean {
  if (result.response.status === 409) {
    return true;
  }
  const message = appleLoginErrorMessage(result).toLowerCase();
  return message.includes('already exists') || message.includes('conflict');
}

/**
 * Backend contract for POST /login/clients/apple (also accepts token aliases).
 * Prefer `identity_token`; send first-sign-in name/email when Apple provides them.
 */
export function buildAppleLoginRequestBody(
  credential: string,
  loginType: LoginOption,
  firstSignInUser?: AppleFirstSignInUser | null,
): Record<string, unknown> {
  const apple = appleProfileFromCredential(credential, firstSignInUser);
  const body: Record<string, unknown> = {
    identity_token: credential,
    // Aliases accepted by the backend
    token: credential,
    id_token: credential,
    apple_id_token: credential,
    credential,
    login_type: canonicalAssetOwnerLoginType(loginType),
  };
  if (apple.email) {
    body.email = apple.email;
  }
  if (apple.firstName) {
    body.given_name = apple.firstName;
    body.first_name = apple.firstName;
  }
  if (apple.lastName) {
    body.family_name = apple.lastName;
    body.last_name = apple.lastName;
  }
  if (apple.fullName) {
    body.full_name = apple.fullName;
  }
  return body;
}

export function buildAppleRegisterRequestBody(
  credential: string,
  loginType: LoginOption,
  firstSignInUser?: AppleFirstSignInUser | null,
): Record<string, unknown> {
  const body = buildAppleLoginRequestBody(credential, loginType, firstSignInUser);
  body.registration_date = new Date().toISOString();
  return body;
}

async function postAppleAuthPath(
  path: string,
  body: Record<string, unknown>,
  flowUsed: 'login' | 'register',
): Promise<AppleLoginApiResult> {
  const anonymousRegister = path.replace(/^\/+/, '').startsWith('register/');
  const { response, urlUsed } = await fetchWithDevProxyFallback(
    path,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      ...(anonymousRegister ? { credentials: 'omit' as RequestCredentials } : {}),
    },
    devProxyRouteForApiPath(path),
  );
  const responseText = await response.text();
  let parsed: unknown = null;
  if (responseText.trim()) {
    try {
      parsed = JSON.parse(responseText) as unknown;
    } catch {
      parsed = null;
    }
  }
  return {
    response,
    urlUsed,
    responseText,
    parsed,
    accessToken: parsed ? extractAccessTokenFromLoginJson(parsed) : null,
    pathUsed: path,
    flowUsed,
  };
}

function shouldTryNextAppleLoginPath(result: AppleLoginApiResult, hasMorePaths: boolean): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (result.response.status === 404) {
    return true;
  }
  return isAppleUserMissingError(result);
}

function shouldTryNextAppleRegisterPath(result: AppleLoginApiResult, hasMorePaths: boolean): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (result.response.status === 404) {
    return true;
  }
  if (result.response.status === 400 || result.response.status === 409 || result.response.status === 422) {
    return true;
  }
  return false;
}

async function tryAppleLoginPaths(
  body: Record<string, unknown>,
  paths: string[],
): Promise<{ result: AppleLoginApiResult | null; sawUserMissing: boolean }> {
  let lastResult: AppleLoginApiResult | null = null;
  let sawUserMissing = false;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const result = await postAppleAuthPath(path, body, 'login');
    if (isSuccessfulAppleLoginResult(result)) {
      return { result, sawUserMissing };
    }
    if (isAppleUserMissingError(result)) {
      sawUserMissing = true;
    }
    lastResult = result;
    if (shouldTryNextAppleLoginPath(result, index < paths.length - 1)) {
      continue;
    }
    return { result, sawUserMissing };
  }

  return { result: lastResult, sawUserMissing };
}

export async function postAppleLoginCredentialOnly(
  credential: string,
  loginType: LoginOption,
  firstSignInUser?: AppleFirstSignInUser | null,
): Promise<AppleLoginApiResult> {
  const loginBody = buildAppleLoginRequestBody(credential, loginType, firstSignInUser);
  const loginPaths = appleLoginPathCandidates(loginType);
  if (loginPaths.length === 0) {
    throw new Error('No Apple login endpoint configured');
  }
  const { result } = await tryAppleLoginPaths(loginBody, loginPaths);
  if (result) {
    return result;
  }
  throw new Error('No Apple login endpoint configured');
}

async function tryAppleLiteRegister(
  credential: string,
  loginType: LoginOption,
  firstSignInUser?: AppleFirstSignInUser | null,
): Promise<AppleLoginApiResult | null> {
  const paths = appleRegisterPathCandidates(loginType);
  if (paths.length === 0) {
    return null;
  }

  const body = buildAppleRegisterRequestBody(credential, loginType, firstSignInUser);
  let lastResult: AppleLoginApiResult | null = null;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const result = await postAppleAuthPath(path, body, 'register');
    if (isSuccessfulAppleLoginResult(result)) {
      return result;
    }
    if (isSuccessfulHttpStatus(result.response.status)) {
      return result;
    }
    lastResult = result;
    if (shouldTryNextAppleRegisterPath(result, index < paths.length - 1)) {
      continue;
    }
    return result;
  }

  return lastResult;
}

/**
 * Prefer login endpoints first — `/login/clients/apple` auto-registers private clients.
 * Lite-register is only a fallback when the API reports a missing user.
 */
export async function postAppleLoginCredential(
  credential: string,
  loginType: LoginOption,
  firstSignInUser?: AppleFirstSignInUser | null,
): Promise<AppleLoginApiResult> {
  const loginBody = buildAppleLoginRequestBody(credential, loginType, firstSignInUser);
  const loginPaths = appleLoginPathCandidates(loginType);
  if (loginPaths.length === 0) {
    throw new Error('No Apple login endpoint configured');
  }

  const firstPass = await tryAppleLoginPaths(loginBody, loginPaths);
  if (firstPass.result && isSuccessfulAppleLoginResult(firstPass.result)) {
    return firstPass.result;
  }

  if (firstPass.sawUserMissing && supportsGoogleLiteRegister(loginType)) {
    const registerResult = await tryAppleLiteRegister(credential, loginType, firstSignInUser);
    if (registerResult && isSuccessfulAppleLoginResult(registerResult)) {
      return registerResult;
    }

    const retryPass = await tryAppleLoginPaths(loginBody, loginPaths);
    if (retryPass.result && isSuccessfulAppleLoginResult(retryPass.result)) {
      return retryPass.result;
    }

    if (registerResult && isSuccessfulHttpStatus(registerResult.response.status)) {
      return registerResult;
    }
    if (retryPass.result) {
      return retryPass.result;
    }
    if (registerResult) {
      return registerResult;
    }
  }

  if (firstPass.result) {
    return firstPass.result;
  }
  throw new Error('No Apple login endpoint configured');
}

export function extractRefreshTokenFromAppleLogin(parsed: unknown): string | null {
  const token = extractRefreshTokenFromUnknown(parsed);
  if (token) {
    clearRefreshUnavailable404();
  }
  return token;
}

export function isSuccessfulAppleLoginResult(result: AppleLoginApiResult): boolean {
  return isSuccessfulHttpStatus(result.response.status) && Boolean(result.accessToken);
}

export function friendlyAppleLoginFailureSummary(
  result: AppleLoginApiResult,
  messages?: {
    notEnabled?: string;
    unavailable?: string;
    loginTypeMismatch?: string;
    userNotRegistered?: string;
    missingToken?: string;
    invalidToken?: string;
  },
): string {
  const apiMessage = appleLoginErrorMessage(result);

  if (result.response.status === 400) {
    return (
      messages?.missingToken ??
      'Apple sign-in did not send an identity token. Try again, or use another sign-in method.'
    );
  }

  if (result.response.status === 401) {
    if (messages?.loginTypeMismatch && /login type/i.test(apiMessage)) {
      return messages.loginTypeMismatch;
    }
    if (isAppleUserMissingError(result) && messages?.userNotRegistered) {
      return messages.userNotRegistered;
    }
    return (
      messages?.invalidToken ??
      'Apple identity token was rejected (invalid, expired, or wrong clientId / audience). Confirm REACT_APP_APPLE_CLIENT_ID is the Services ID ai.offizz.dev.'
    );
  }

  if (isAppleUserMissingError(result) && messages?.userNotRegistered) {
    return messages.userNotRegistered;
  }

  if (result.response.status === 404) {
    return (
      messages?.notEnabled ??
      'Apple sign-in is not enabled on the server yet. Ask your backend team to add an Apple login endpoint, or sign in with username and password below.'
    );
  }
  if (result.response.status === 501) {
    return (
      messages?.unavailable ??
      'Apple sign-in is not available on this server yet. Use username and password below, or try again later.'
    );
  }
  return friendlyHttpFailureSummary(result.response.status, result.response.statusText, result.responseText);
}

/** Safe summary for error debug panels — never log the raw Apple ID token. */
export function appleLoginDebugRequestPayload(
  credential: string,
  loginType: LoginOption,
  result: Pick<AppleLoginApiResult, 'pathUsed' | 'flowUsed'>,
  firstSignInUser?: AppleFirstSignInUser | null,
): Record<string, unknown> {
  const apple = appleProfileFromCredential(credential, firstSignInUser);
  const payload: Record<string, unknown> = {
    provider: 'apple',
    login_type: canonicalAssetOwnerLoginType(loginType),
    path: result.pathUsed,
    flow: result.flowUsed,
    credential: '(Apple ID token — sent, omitted from logs)',
    email: apple.email || undefined,
  };
  if (apple.firstName) {
    payload.given_name = apple.firstName;
  }
  if (apple.lastName) {
    payload.family_name = apple.lastName;
  }
  payload.note =
    'identity_token sent (omitted from logs). Name/email only available on Apple first authorization.';
  return payload;
}

export const APPLE_SIGN_IN_EXTERNAL_REDIRECT = 'AppleSignInExternalRedirect';

export function isAppleSignInExternalRedirect(error: unknown): boolean {
  return error instanceof Error && error.name === APPLE_SIGN_IN_EXTERNAL_REDIRECT;
}

export function isAppleSignInCancelled(error: unknown): boolean {
  if (isAppleSignInExternalRedirect(error)) {
    return true;
  }
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  return /cancel|popup closed|user closed|1001|authorization error/i.test(message);
}

/** Web Apple Sign-In popup — returns identity token and optional first-sign-in user payload. */
export async function requestAppleWebSignIn(
  clientId: string,
  redirectURI: string,
  loginType: LoginOption,
): Promise<{ idToken: string; user?: AppleFirstSignInUser }> {
  if (!shouldUseAppleJsPopup()) {
    const state = createAppleOAuthState(loginType);
    persistAppleSignInPending(loginType, state);
    window.location.assign(buildAppleAuthorizeUrl(clientId, redirectURI, state));
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 1200);
    });
    const redirected = new Error('Apple Sign-In continues in the system browser');
    redirected.name = APPLE_SIGN_IN_EXTERNAL_REDIRECT;
    throw redirected;
  }
  await loadAppleSignInScript();
  ensureAppleSignInInitialized(clientId, redirectURI);
  const apple = window.AppleID?.auth;
  if (!apple) {
    throw new Error('Apple Sign-In SDK did not load');
  }
  const data = await apple.signIn();
  const idToken = data.authorization?.id_token?.trim();
  if (!idToken) {
    throw new Error('Apple Sign-In did not return an ID token');
  }
  return {
    idToken,
    user: data.user,
  };
}
